import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DateField } from '../DateField/DateField';
import SearchInput from '../SearchInput/SearchInput';
import './TableFilterBar.scss';

export type FilterOption = {
  value: string;
  label: string;
};

export type DateRangeValue = {
  from: string;
  to: string;
};

export type TableFilterDefinition =
  | {
      id: string;
      label: string;
      type: 'multiSelect';
      value: string[];
      onChange: (value: string[]) => void;
      options: FilterOption[];
    }
  | {
      id: string;
      label: string;
      type: 'singleSelect';
      value: string;
      onChange: (value: string) => void;
      options: FilterOption[];
      /** The value that counts as "unset" — the filter reads active only when off it. */
      defaultValue?: string;
    }
  | {
      id: string;
      label: string;
      type: 'dateRange';
      value: DateRangeValue;
      onChange: (value: DateRangeValue) => void;
    };

const EMPTY_DATE_RANGE: DateRangeValue = { from: '', to: '' };
// const ADD_FILTER_MENU_ID = '__menu__';

/** Space kept between the filter popover and the viewport edge when it has to shift. */
const EDITOR_VIEWPORT_GUTTER = 12;

/**
 * Keeps an open filter popover inside the viewport.
 *
 */
function useKeepEditorInViewport(editorRef: React.RefObject<HTMLDivElement | null>) {
  const [offsetLeft, setOffsetLeft] = useState(0);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    const anchor = editor?.parentElement;
    if (!editor || !anchor) return;

    const reposition = () => {
      const anchorLeft = anchor.getBoundingClientRect().left;
      const overflowRight =
        anchorLeft + editor.offsetWidth - (window.innerWidth - EDITOR_VIEWPORT_GUTTER);

      if (overflowRight <= 0) {
        setOffsetLeft(0);
        return;
      }

      const maxShift = Math.max(0, anchorLeft - EDITOR_VIEWPORT_GUTTER);
      setOffsetLeft(-Math.min(overflowRight, maxShift));
    };

    reposition();

    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [editorRef]);

  return offsetLeft;
}

export function TableFilterBar({ filters }: { filters: TableFilterDefinition[] }) {
  const [openFilterId, setOpenFilterId] = useState('');

  const menuRef = useRef<HTMLDivElement | null>(null);
  const addButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;

      if (menuRef.current?.contains(target)) return;
      if (addButtonRef.current?.contains(target)) return;

      setOpenFilterId('');
    };

    if (openFilterId) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openFilterId]);

  const activeFilters = useMemo(() => {
    return filters.filter((filter) => {
      if (filter.type === 'multiSelect') return filter.value.length > 0;

      if (filter.type === 'singleSelect') return filter.value !== (filter.defaultValue ?? '');

      return Boolean(filter.value.from || filter.value.to);
    });
  }, [filters]);

  const clearFilter = (filter: TableFilterDefinition) => {
    if (filter.type === 'multiSelect') {
      filter.onChange([]);
    } else if (filter.type === 'singleSelect') {
      filter.onChange(filter.defaultValue ?? '');
    } else {
      filter.onChange(EMPTY_DATE_RANGE);
    }
  };

  const clearAllFilters = () => {
    activeFilters.forEach(clearFilter);
    setOpenFilterId('');
  };

  const getButtonText = (filter: TableFilterDefinition) => {
    if (filter.type === 'multiSelect') {
      if (!filter.value.length) return `${filter.label}: All`;

      const labels = filter.options
        .filter((option) => filter.value.includes(option.value))
        .map((option) => option.label);

      if (labels.length === 0) return `${filter.label}: ${filter.value.length} selected`;

      return `${filter.label}: ${labels.slice(0, 1).join(', ')}${
        labels.length > 1 ? ` +${labels.length - 1}` : ''
      }`;
    }

    if (filter.type === 'singleSelect') {
      const selected = filter.options.find((option) => option.value === filter.value);

      return `${filter.label}: ${selected ? selected.label : 'All'}`;
    }

    if (!filter.value.from && !filter.value.to) return filter.label;

    if (filter.value.from && filter.value.to) {
      return `${filter.label}: ${filter.value.from} → ${filter.value.to}`;
    }

    if (filter.value.from) return `${filter.label}: from ${filter.value.from}`;

    return `${filter.label}: until ${filter.value.to}`;
  };

  return (
    <div className="table-filter-bar table-filter-bar--inline">
      <div className="table-filter-bar__inline-row">
        {filters.map((filter) => {
          const isActive =
            filter.type === 'multiSelect'
              ? filter.value.length > 0
              : filter.type === 'singleSelect'
                ? filter.value !== (filter.defaultValue ?? '')
                : Boolean(filter.value.from || filter.value.to);

          return (
            <div key={filter.id} className="table-filter-bar__inline-item">
              <button
                type="button"
                className={`table-filter-bar__dropdown-btn ${isActive ? 'is-active' : ''}`}
                onClick={() =>
                  setOpenFilterId((current) => (current === filter.id ? '' : filter.id))
                }
              >
                <span>{getButtonText(filter)}</span>
                <span aria-hidden="true">▾</span>
              </button>

              {openFilterId === filter.id && (
                <FilterEditor
                  filter={filter}
                  onClose={() => setOpenFilterId('')}
                  menuRef={menuRef}
                />
              )}
            </div>
          );
        })}

        {activeFilters.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--xs table-filter-bar__clear-all"
            onClick={clearAllFilters}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

function FilterEditor({
  filter,
  onClose,
  menuRef,
}: {
  filter: TableFilterDefinition;
  onClose: () => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [draftMulti, setDraftMulti] = useState<string[]>(
    filter.type === 'multiSelect' ? filter.value : []
  );

  const [draftDate, setDraftDate] = useState<DateRangeValue>(
    filter.type === 'dateRange' ? filter.value : EMPTY_DATE_RANGE
  );

  const [draftSingle, setDraftSingle] = useState<string>(
    filter.type === 'singleSelect' ? filter.value : ''
  );

  const [search, setSearch] = useState('');

  const offsetLeft = useKeepEditorInViewport(menuRef);

  const options =
    filter.type === 'multiSelect'
      ? [...filter.options]
          .filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase()))
          .sort((a, b) => {
            if (['delivery-state', 'status', 'pickup-state', 'load-option'].includes(filter.id)) {
              return a.label.localeCompare(b.label);
            }

            const aSelected = draftMulti.includes(a.value);
            const bSelected = draftMulti.includes(b.value);

            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;

            return a.label.localeCompare(b.label);
          })
      : [];

  const apply = () => {
    if (filter.type === 'multiSelect') {
      filter.onChange(draftMulti);
    } else if (filter.type === 'singleSelect') {
      filter.onChange(draftSingle);
    } else {
      filter.onChange(draftDate);
    }

    onClose();
  };

  return (
    <div ref={menuRef} className="table-filter-bar__editor" style={{ left: offsetLeft }}>
      <div className="table-filter-bar__editor-title">{filter.label}</div>

      {filter.type === 'multiSelect' && (
        <>
          <SearchInput
            type="text"
            className="table-filter-bar__search"
            placeholder="Search…"
            value={search}
            onValueChange={setSearch}
          />

          <div className="table-filter-bar__checks">
            {options.length === 0 && <div className="muted tiny">No options found.</div>}

            {options.map((option) => (
              <label key={option.value}>
                <input
                  type="checkbox"
                  checked={draftMulti.includes(option.value)}
                  onChange={(e) => {
                    setDraftMulti((previous) =>
                      e.target.checked
                        ? [...previous, option.value]
                        : previous.filter((value) => value !== option.value)
                    );
                  }}
                />

                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </>
      )}

      {filter.type === 'singleSelect' && (
        <div className="table-filter-bar__checks">
          {filter.options.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name={`single-${filter.id}`}
                checked={draftSingle === option.value}
                onChange={() => setDraftSingle(option.value)}
              />

              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {filter.type === 'dateRange' && (
        <div className="table-filter-bar__date-range">
          <label>
            <span>From</span>
            <DateField
              value={draftDate.from}
              onChange={(from) => setDraftDate((previous) => ({ ...previous, from }))}
            />
          </label>

          <label>
            <span>To</span>
            <DateField
              value={draftDate.to}
              onChange={(to) => setDraftDate((previous) => ({ ...previous, to }))}
            />
          </label>
        </div>
      )}

      <div className="table-filter-bar__actions">
        <button type="button" className="btn btn--ghost btn--xs" onClick={onClose}>
          Cancel
        </button>

        <button type="button" className="btn btn--primary btn--xs" onClick={apply}>
          Apply
        </button>
      </div>
    </div>
  );
}
